import {clamp} from '@benchling/js-utils';
import {createStyles, Modal as StructureModal} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {isNodeTesting} from 'lib/Browser';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

interface ChemicalStructureViewerProps {
  title: string;
  base64Svg: string;
  opened: boolean;
  onClose: () => void;
}

const INITIAL_POSITION = {x: 0, y: 0};
const DEFAULT_SCALE = 1;
const MIN_SCALE = 0.1;
const MAX_SCALE = 5;

const useStyles = createStyles(() => ({
  modalBody: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

export default function ChemicalStructureViewer(props: ChemicalStructureViewerProps): JSX.Element {
  const {title, base64Svg, onClose, opened} = props;
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [position, setPosition] = useState(INITIAL_POSITION);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(INITIAL_POSITION);

  const {classes} = useStyles();

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>): void => {
    // normalize the delta for consistent behavior across mouse wheel (typically stepped values)
    // and touchpad (more "continuous" values)
    const delta = e.deltaY;
    const zoomIntensity = 0.001;

    setScale((prevScale) => {
      const newScale = prevScale - delta * zoomIntensity;
      return clamp(MIN_SCALE, MAX_SCALE, newScale);
    });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!isDragging) {
      return;
    }
    e.preventDefault();
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = (): void => {
    setIsDragging(false);
  };

  const handleMouseLeave = (): void => {
    setIsDragging(false);
  };

  const handleReset = (): void => {
    setPosition(INITIAL_POSITION);
    setScale(DEFAULT_SCALE);
  };

  const handleClose = (): void => {
    onClose();
    // delay to avoid resetting the position and scale before the modal fades out
    setTimeout(() => {
      handleReset();
    }, 200);
  };

  return (
    <StructureModal
      title={title}
      size="xl"
      fullScreen={true}
      radius={0}
      opened={opened}
      onClose={handleClose}
      classNames={{body: classes.modalBody, content: classes.modalContent}}
      withinPortal={!isNodeTesting}
    >
      <div className={css(styles.contentInfo)}>
        <p>Use the mouse wheel or touch pad to zoom in and out, and drag to move the structure.</p>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          icon="undo"
          disabled={position.x === INITIAL_POSITION.x && position.y === INITIAL_POSITION.y && scale === 1}
          size={ButtonSize.SMALL}
          text="Reset"
          onClick={handleReset}
        />
      </div>
      <div
        className={css(styles.structureContainer, isDragging && styles.dragging)}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <img
          {...getTestProps({component: 'ChemicalStructureViewer', element: 'chemicalStructure'})}
          className={css(styles.fullScreenStructure)}
          src={`data:image/svg+xml;base64,${base64Svg}`}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          }}
        />
      </div>
    </StructureModal>
  );
}

const styles = StyleSheet.create({
  contentInfo: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  structureContainer: {
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden',
    cursor: 'grab',
  },
  dragging: {
    cursor: 'grabbing',
  },
  fullScreenStructure: {
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
    display: 'block',
    transformOrigin: 'center',
  },
});
